import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, Phase } from '../lib/api';
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export default function PhaseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPhase();
  }, [slug]);

  const loadPhase = async () => {
    if (!slug) return;

    try {
      const response = await api.getPhaseDetail(slug);
      setPhase(response);
    } catch (error) {
      console.error('Failed to load phase:', error);
      setError('Failed to load phase details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartPhase = async () => {
    if (!slug || !phase) return;

    setIsExecuting(true);
    setError('');

    try {
      await api.startPhase(slug);
      await loadPhase();
      navigate(`/phases/${slug}/review`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start phase');
    } finally {
      setIsExecuting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!phase) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Phase not found</p>
        <Link to="/" className="btn btn-primary mt-4">
          Return to dashboard
        </Link>
      </div>
    );
  }

  const statusColor = phase.status === 'completed' ? 'sage' : phase.status === 'available' ? 'teal' : phase.status === 'pending_review' ? 'copper' : 'charcoal';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-charcoal-300 hover:text-copper-600 transition-colors text-sm">
        <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
        Back to dashboard
      </Link>

      {/* Phase Header */}
      <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-[radial-gradient(circle,rgba(196,117,76,0.05),transparent_70%)]" />
          <svg className="absolute right-4 top-4 w-24 h-24 opacity-[0.03]" viewBox="0 0 100 100">
            <circle cx="80" cy="20" r="40" fill="none" stroke="#2C3E3D" strokeWidth="1" />
            <circle cx="80" cy="20" r="25" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
          </svg>
        </div>

        {/* Accent strip */}
        <div className={`h-1 bg-gradient-to-r ${
          statusColor === 'sage' ? 'from-sage-400 to-sage-500' :
          statusColor === 'teal' ? 'from-teal-400 to-teal-500' :
          statusColor === 'copper' ? 'from-copper-400 to-copper-500' :
          'from-charcoal-200 to-charcoal-300'
        }`} />

        <div className="relative p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="inline-flex items-center gap-2 bg-charcoal-50 rounded-full px-3 py-1 text-xs font-semibold text-charcoal-400 mb-3">
                <span className={`w-1.5 h-1.5 rounded-full bg-${statusColor}-500`} />
                Phase {phase.number}
              </div>
              <h1 className="text-3xl font-bold text-charcoal-500">{phase.name}</h1>
            </div>
            {phase.status === 'completed' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sage-50 border border-sage-200 text-sage-600 text-sm font-medium">
                <CheckCircleIcon className="h-4 w-4" />
                Completed
              </span>
            )}
          </div>

          <p className="text-charcoal-400 text-lg leading-relaxed">{phase.description}</p>

          {/* Phase Details */}
          <div className="mt-6 pt-6 border-t border-charcoal-100 grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 rounded-xl bg-charcoal-50/50">
              <div className="text-charcoal-300 mb-1 text-xs uppercase tracking-wide font-medium">AI Agents</div>
              <div className="font-semibold text-charcoal-500">{phase.agents.join(', ')}</div>
            </div>
            <div className="p-3 rounded-xl bg-charcoal-50/50">
              <div className="text-charcoal-300 mb-1 text-xs uppercase tracking-wide font-medium">Revision Budget</div>
              <div className="font-semibold text-charcoal-500">{phase.revision_budget} revisions included</div>
            </div>
          </div>

          {/* Dependencies */}
          {phase.depends_on && phase.depends_on.length > 0 && (
            <div className="mt-4 p-4 bg-cream-50 rounded-xl border border-cream-200">
              <p className="text-sm text-charcoal-400">
                <strong className="text-charcoal-500">Requires:</strong> Complete Phase{phase.depends_on.length > 1 ? 's' : ''} {phase.depends_on.join(', ')} first
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex items-center gap-4">
            {phase.status === 'available' && (
              <button
                onClick={handleStartPhase}
                disabled={isExecuting}
                className="btn btn-primary flex items-center disabled:opacity-50 shadow-sm shadow-copper-500/20"
              >
                {isExecuting ? (
                  <>
                    <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                    Executing agents...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Start Phase
                  </>
                )}
              </button>
            )}

            {phase.status === 'completed' && (
              <Link to={`/phases/${slug}/review`} className="btn btn-primary flex items-center shadow-sm shadow-copper-500/20">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                View Content
              </Link>
            )}

            {phase.status === 'locked' && (
              <button disabled className="btn btn-secondary opacity-50 cursor-not-allowed">
                Phase Locked
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Phase Deliverables */}
      {phase.deliverables && phase.deliverables.length > 0 && (
        <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
          <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none opacity-[0.03]">
            <svg viewBox="0 0 800 40" preserveAspectRatio="none" className="w-full h-full">
              <path d="M0,40 L0,25 L100,12 L200,22 L300,8 L400,18 L500,5 L600,20 L700,10 L800,30 L800,40 Z" fill="#2C3E3D" />
            </svg>
          </div>

          <div className="relative p-6">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="h-5 w-5 text-copper-400" />
              <h2 className="text-xl font-bold text-charcoal-500">What You'll Get</h2>
            </div>
            <ul className="space-y-3">
              {phase.deliverables.map((deliverable, index) => (
                <li key={index} className="flex items-start gap-3 p-2 rounded-lg hover:bg-cream-50 transition-colors">
                  <CheckCircleIcon className="h-5 w-5 text-sage-500 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-400">{formatDeliverable(deliverable)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDeliverable(key: string): string {
  const deliverables: Record<string, string> = {
    scout_report: 'Comprehensive business intelligence report',
    seo_report: 'Local SEO competitive analysis',
    google_reviews: 'Google Reviews sentiment analysis',
    compliance_profile: 'Regulatory compliance profile',
    brand_guide: 'Complete brand style guide',
    website_pages: '12-page SEO-optimized website',
    funnel_pages: '4-step conversion funnel',
    blog_calendar: '12-month blog content calendar',
    social_calendar: 'Social media posting schedule',
    email_sequences: 'Automated email campaigns',
    sms_campaigns: 'SMS marketing templates',
    activation_package: 'Complete system deployment',
  };

  return deliverables[key] || key;
}
